import { MessageType, useUpdateFrequencyCapMutation } from 'api/MessageTypeAPI';
import { SubscriptionPolicy } from 'modules/graphql';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { ApiError } from 'services/api';
import styled from 'styled-components';
import { BUTTON_SIZES, GhostButton, PrimaryButton } from 'ui/button';
import { CHECKBOX_STATUS, Checkbox } from 'ui/checkbox';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { colors } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { useChannelsContext } from '../../contexts/ChannelsContext';
import { HeaderWrapper } from './UpdateFrequencyCapModal/Styled';

const CheckboxWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: ${pxToRem(11)};
`;

const LabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  & label {
    font-size: ${pxToRem(14)};
  }
  & p {
    color: ${colors.secondaryText};
    font-size: ${pxToRem(12)};
  }
`;

export interface ModalProps {
  onClose: () => void;
  messageType: MessageType;
}

export const IGNORE_FREQUENCY_CAP_TEXT = {
  HEADER: 'Ignore frequency cap',
  DESCRIPTION: 'Messages will be sent even if the user has reached the project frequency cap',
  ACTION_BUTTON: 'Save setting',
  SUCCESS_MESSAGE: 'Success! Updated frequency management: ',
  ERROR_MESSAGE: 'Unable to update frequency management. ',
  CHECKBOX_NAME: 'ignore-frequency-cap',
};

export const IgnoreFrequencyCapModal = ({ onClose, messageType }: ModalProps) => {
  const { name: messageTypeName, frequencyCap } = messageType;
  const [ignoreFrequencyCap, setignoreFrequencyCap] = useState(
    frequencyCap?.ignoreFrequencyCap || false,
  );
  const { refetchMessageTypes } = useChannelsContext();
  const dispatch = useDispatch();
  const { mutate } = useUpdateFrequencyCapMutation({
    onSuccess: () => {
      onClose();
      dispatch(
        addPositiveToast(
          <>
            {IGNORE_FREQUENCY_CAP_TEXT.SUCCESS_MESSAGE} <strong>{messageTypeName}</strong>
          </>,
        ),
      );
      refetchMessageTypes();
    },
    onError: (e: unknown) => {
      const { response } = e as ApiError;
      const errorText = `${IGNORE_FREQUENCY_CAP_TEXT.ERROR_MESSAGE} ${response?.data}.`;
      onClose();
      dispatch(addNegativeToastWithErrorId(errorText, 'useUpdateFrequencyCapMutation', e));
    },
  });

  const onSubmit = () => {
    // Usage of the message type update API needs to send DOI fields at the top level of the object in the update request
    const doiFields =
      messageType.subscriptionPolicy === SubscriptionPolicy.DoubleOptIn
        ? {
            brandName: messageType.subscriptionPolicyMetadata?.brandName,
            messagingInitiative: messageType.subscriptionPolicyMetadata?.messagingInitiative,
            disclaimerLink: messageType.subscriptionPolicyMetadata?.disclaimerLink,
            smsSenderId: messageType.subscriptionPolicyMetadata?.smsSenderId,
          }
        : {};

    const payload = { ...messageType, ...doiFields, ignoreFrequencyCap };
    mutate(payload);
  };

  return (
    <Modal
      id="ignore-frequency-cap-modal"
      isModalOpen={true}
      onClose={onClose}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>
        <HeaderWrapper>{IGNORE_FREQUENCY_CAP_TEXT.HEADER}?</HeaderWrapper>
      </ModalHeader>
      <ModalBody>
        <CheckboxWrapper>
          <Checkbox
            name={IGNORE_FREQUENCY_CAP_TEXT.CHECKBOX_NAME}
            onClick={() => setignoreFrequencyCap((currentStatus) => !currentStatus)}
            status={ignoreFrequencyCap ? CHECKBOX_STATUS.CHECKED : CHECKBOX_STATUS.UNCHECKED}
          />
          <LabelWrapper>
            <label>{IGNORE_FREQUENCY_CAP_TEXT.HEADER}</label>
            <p>{IGNORE_FREQUENCY_CAP_TEXT.DESCRIPTION}</p>
          </LabelWrapper>
        </CheckboxWrapper>
      </ModalBody>
      <ModalFooter hasCancel={false}>
        <GhostButton
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          size={BUTTON_SIZES.LARGE}
        >
          Cancel
        </GhostButton>
        <PrimaryButton
          onClick={onSubmit}
          isDisabled={frequencyCap?.ignoreFrequencyCap === ignoreFrequencyCap}
          size={BUTTON_SIZES.LARGE}
        >
          {IGNORE_FREQUENCY_CAP_TEXT.ACTION_BUTTON}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
